import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { type DefaultRootState } from '../redux';

import {
  getDatabaseWithCurrentView,
  selectFocusedItem,
  selectIsEntireEntrySelected,
  selectIsEntireFieldSelected,
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldIds,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from './selectors';
import cellSelectionsSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

/**
 * Selects the range of fields from the most recently selected field to the field with the given ID.
 * If there is no most recently selected field, only selects the specified field.
 * Unselects any selected entries or values, but does not unselect any already selected fields.
 */
const addFieldRange =
  (contentId: string, fieldId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();
    const databaseDraft = getDatabaseWithCurrentView(state, {
      contentId,
    });

    const lastSelection = selectFocusedItem(state, {
      contentId,
      type: 'field',
    });

    const currentSelectedFieldIndex = databaseDraft.fields.indexOf(fieldId);
    const lastSelectedFieldIndex = lastSelection
      ? databaseDraft.fields.indexOf(lastSelection.fieldId)
      : currentSelectedFieldIndex;
    const rangedFieldIds =
      lastSelectedFieldIndex < currentSelectedFieldIndex
        ? databaseDraft.fields.slice(
            lastSelectedFieldIndex,
            currentSelectedFieldIndex + 1
          )
        : databaseDraft.fields.slice(
            currentSelectedFieldIndex,
            lastSelectedFieldIndex + 1
          );

    dispatch(
      cellSelectionsSlice.actions.selectFields({
        contentId,
        fieldIds: rangedFieldIds,
        clear: false,
        focusFieldId: fieldId,
      })
    );
  };

/**
 * Selects the range of entries from the most recently selected entry to the entry with the specified ID.
 * If there is no most recently selected entry, selects only the specified entry.
 * Unselects any selected values or fields, but does not unselect any already selected entries.
 */
const addEntryRange =
  (contentId: string, entryId: string): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();
    const databaseDraft = getDatabaseWithCurrentView(state, {
      contentId,
    });

    const lastSelection = selectFocusedItem(state, {
      contentId,
      type: 'entry',
    });

    const currentSelectedEntryIndex = databaseDraft.entries.indexOf(entryId);
    const lastSelectedEntryIndex = lastSelection
      ? databaseDraft.entries.indexOf(lastSelection.entryId)
      : currentSelectedEntryIndex;
    const rangedEntryIds =
      lastSelectedEntryIndex < currentSelectedEntryIndex
        ? databaseDraft.entries.slice(
            lastSelectedEntryIndex,
            currentSelectedEntryIndex + 1
          )
        : databaseDraft.entries.slice(
            currentSelectedEntryIndex,
            lastSelectedEntryIndex + 1
          );

    dispatch(
      cellSelectionsSlice.actions.selectEntries({
        contentId,
        entryIds: rangedEntryIds,
        clear: false,
        focusEntryId: entryId,
      })
    );
  };

/**
 * Selects the value with the specified field/entry ID.
 *
 * @param clearExistingSelection - If true, clears the current selection before selecting the new value. Defaults to true.
 */
export const selectValueSingle = ({
  contentId,
  entryId,
  fieldId,
  clearExistingSelection = true,
}: {
  contentId: string;
  entryId: string;
  fieldId: string;
  clearExistingSelection?: boolean;
}): AnyAction => {
  return cellSelectionsSlice.actions.selectValues({
    contentId,
    values: [{ fieldId, entryId }],
    clear: clearExistingSelection,
    focusValue: { fieldId, entryId },
  });
};

/**
 * Clears the selection and selects the entire field with the given ID. If only the field
 * is already selected, unselects it instead.
 */
export const selectFieldSingle =
  ({ contentId, fieldId }: { contentId: string; fieldId: string }): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();

    const selectedFieldIds = selectSelectedEntireFieldIds(state, {
      contentId,
    });
    if (selectedFieldIds.length === 1 && selectedFieldIds[0] === fieldId) {
      dispatch(
        cellSelectionsSlice.actions.deselectFields({
          fieldIds: [fieldId],
          focusFieldId: fieldId,
        })
      );
    } else {
      dispatch(
        cellSelectionsSlice.actions.selectFields({
          contentId,
          fieldIds: [fieldId],
          clear: true,
          focusFieldId: fieldId,
        })
      );
    }
  };

/**
 * Clears the selection and selects the entire entry with the given ID. If only the entry
 * is already selected, it is unselected instead.
 */
export const selectEntrySingle =
  ({ contentId, entryId }: { contentId: string; entryId: string }): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();

    const selectedEntryIds = selectSelectedEntireEntryIds(state, {
      contentId,
    });
    if (selectedEntryIds.length === 1 && selectedEntryIds[0] === entryId) {
      dispatch(
        cellSelectionsSlice.actions.deselectEntries({
          contentId,
          entryIds: [entryId],
          focusEntryId: entryId,
        })
      );
    } else {
      dispatch(
        cellSelectionsSlice.actions.selectEntries({
          contentId,
          entryIds: [entryId],
          clear: true,
          focusEntryId: entryId,
        })
      );
    }
  };

/**
 * Selects the range of values between the currently selected values and the value with the
 * given field/entry ID. If there is no value is currently selected, selects only the specified value.
 * Unselects any selected entire fields/entries, but does not unselect any values that are already selected.
 */
export const selectValueRange =
  ({
    contentId,
    entryId,
    fieldId,
  }: {
    contentId: string;
    entryId: string;
    fieldId: string;
  }): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();
    const databaseDraft = getDatabaseWithCurrentView(state, {
      contentId,
    });

    const selectedEntryIds = selectSelectedEntryIds(state, {
      contentId,
    });
    const selectedEntryIdxs = selectedEntryIds.map((entryId) =>
      databaseDraft.entries.indexOf(entryId)
    );
    const newEntryIdx = databaseDraft.entries.indexOf(entryId);
    const newEntryIds = databaseDraft.entries.slice(
      Math.min(...selectedEntryIdxs, newEntryIdx),
      Math.max(...selectedEntryIdxs, newEntryIdx) + 1
    );

    const selectedFieldIds = selectSelectedFieldIds(state, {
      contentId,
    });
    const selectedFieldIdxs = selectedFieldIds.map((fieldId) =>
      databaseDraft.fields.indexOf(fieldId)
    );
    const newFieldIdx = databaseDraft.fields.indexOf(fieldId);
    const newFieldIds = databaseDraft.fields.slice(
      Math.min(...selectedFieldIdxs, newFieldIdx),
      Math.max(...selectedFieldIdxs, newFieldIdx) + 1
    );

    dispatch(
      cellSelectionsSlice.actions.selectValues({
        contentId,
        values: newFieldIds.flatMap((fieldId) =>
          newEntryIds.map((entryId) => ({ fieldId, entryId }))
        ),
        clear: true,
        focusValue: { fieldId, entryId },
      })
    );
  };

/**
 * Toggles the selection of the entire entry with the given ID in addition to the currently
 * selected entries. If "isRangeSelect" is true, selects all entries and their values between the most recently
 * selected entry and the specified one.
 * If there is no most recently selected entry, selects only the specified entry and its values.
 */
export const selectEntryMulti =
  ({
    contentId,
    entryId,
    isRangeSelect,
  }: {
    contentId: string;
    entryId: string;
    /** If true, additionally selects all the entries between the most recently selected entry and the specified one. */
    isRangeSelect: boolean;
  }): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();

    if (isRangeSelect) {
      dispatch(addEntryRange(contentId, entryId));
    } else if (selectIsEntireEntrySelected(state, { entryId })) {
      dispatch(
        cellSelectionsSlice.actions.deselectEntries({
          contentId,
          entryIds: [entryId],
          focusEntryId: entryId,
        })
      );
    } else {
      dispatch(
        cellSelectionsSlice.actions.selectEntries({
          contentId,
          entryIds: [entryId],
          clear: false,
          focusEntryId: entryId,
        })
      );
    }
  };

/**
 * Toggles the selection of the entire field with the given ID and all its values in addition to the currently
 * selected fields. If "isRangeSelect" is true, selects all fields and their values between the most recently
 * selected field and the specified one.
 * If there is no most recently selected field, selects only the specified field and its values.
 */
export const selectFieldMulti =
  ({
    contentId,
    fieldId,
    isRangeSelect,
  }: {
    contentId: string;
    fieldId: string;
    /** If true, additionally selects all the fields between the most recently selected field and the specified one. */
    isRangeSelect: boolean;
  }): Dispatcher<void> =>
  (dispatch, getState) => {
    const state = getState();

    if (isRangeSelect) {
      dispatch(addFieldRange(contentId, fieldId));
    } else if (selectIsEntireFieldSelected(state, { fieldId })) {
      dispatch(
        cellSelectionsSlice.actions.deselectFields({
          fieldIds: [fieldId],
          focusFieldId: fieldId,
        })
      );
    } else {
      dispatch(
        cellSelectionsSlice.actions.selectFields({
          contentId,
          fieldIds: [fieldId],
          clear: false,
          focusFieldId: fieldId,
        })
      );
    }
  };

/**
 * Clears the current selection according to boolean flags and selects the entire entries with the given IDs and their values.
 * @param entryIds ids used for selecting entries
 * @param clearSelectedValues: indicates if selected values should be cleared, defaults to true
 */
export const selectEntries =
  ({
    contentId,
    entryIds,
    clearSelectedValues = true,
  }: {
    contentId: string;
    entryIds: string[];
    clearSelectedValues?: boolean;
  }): Dispatcher<void> =>
  (dispatch) => {
    dispatch(
      cellSelectionsSlice.actions.selectEntries({
        contentId,
        entryIds,
        clear: true,
        focusEntryId: entryIds[entryIds.length - 1],
        clearSelectedValues,
      })
    );
  };

/**
 * Deselects entries specified in the entryIds array
 * @param entryIds: entry ids to un-select
 */
export const deselectEntries =
  ({
    contentId,
    entryIds,
  }: {
    contentId: string;
    entryIds: string[];
  }): Dispatcher<void> =>
  (dispatch) => {
    dispatch(
      cellSelectionsSlice.actions.deselectEntries({
        contentId,
        entryIds: entryIds,
        focusEntryId: undefined,
      })
    );
  };

/**
 * Clears the current selection and selects the entire fields with the given IDs and their values.
 */
export const selectColumns =
  ({
    contentId,
    fieldIds,
    clearSelectedValues = true,
  }: {
    contentId: string;
    fieldIds: string[];
    clearSelectedValues?: boolean;
  }): Dispatcher<void> =>
  (dispatch) => {
    dispatch(
      cellSelectionsSlice.actions.selectFields({
        contentId,
        fieldIds,
        clear: true,
        focusFieldId: fieldIds[fieldIds.length - 1],
        clearSelectedValues,
      })
    );
  };

/**
 * Deselects columns specified in the fieldIds array
 * @param fieldIds: field ids to de-select
 */
export const deselectColumns =
  ({ fieldIds }: { fieldIds: string[] }): Dispatcher<void> =>
  (dispatch) => {
    dispatch(
      cellSelectionsSlice.actions.deselectFields({
        fieldIds: fieldIds,
        focusFieldId: undefined,
      })
    );
  };

export const addEntryToValueRange =
  ({ contentId, entryId }: { contentId: string; entryId: string }): Dispatcher<void> =>
  (dispatch, getState) => {
    const selectedFieldIds = selectSelectedFieldIds(getState(), {
      contentId,
    });
    batch(() =>
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        selectedFieldIds.forEach((fieldId) => {
          dispatch(
            selectValueRange({
              contentId,
              entryId: entryId,
              fieldId: fieldId,
            })
          );
        });
      })
    );
  };

export const {
  deselectAll,
  selectValues,
  setSoftSelection,
  setHoverDeleteButton,
  setFirstSelectedValue,
} = cellSelectionsSlice.actions;
